import { Button, Card, Empty, Input, InputNumber, Radio, Select, Space, Timeline, Tooltip, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { StatusTag } from "@/views/components/StatusTag";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import request from "@/api";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import OperationHistoryPanel from "@/views/components/OperationHistoryPanel";
import { useUserStore } from "@/views/stores";
import { getEnterpriseName } from "@/utils/customer";
import { areaLib } from "@/utils/areaLib";
import { fmtMoney } from "@/utils/money";
import { dateTimeFormat } from "@/utils";
import { confirmDialog } from "@/utils/confirmDialog";
import { showRequestError } from "@/utils/requestErrorMessage";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import { fileService } from "@/api/services/fileService";
import { orderRemarkService } from "@/api/services/orderRemarkService";
import type {
    CancelReceivePaymentForm,
    ConfirmReceivePaymentForm,
    DeliveryVo,
    Invoice,
    OrderFinishForm,
    OrderVo,
    OrderRemark,
    ReceivePayment,
    ReceivePaymentForm,
    Refund,
    RefundForm,
    ServiceSpecialVo
} from "@zhihaoscm/entity";
import {
    BARGAIN_END_FLAG,
    GROUP_PURCHASE_STATE,
    ORDER_TYPE,
    ORDER_PRICE_TYPE,
    RECEIVE_PAYMENT_TYPE,
    RECEIVE_STATE,
    SETTLEMENT_METHOD,
    DELIVERY_STATE
} from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    Table as ZhTable,
    Tabs as ZhTabs,
    Upload as ZhUpload,
    type TableColumnProps,
    type UploadRequestOptions
} from "zhihao-ui-react";
import { TradeOrderCancelDialog } from "./TradeOrderCancelDialog";
import { TradeOrderFinishDialog } from "./TradeOrderFinishDialog";
import { TradeOrderSchedulePanel } from "./TradeOrderSchedulePanel";
import { REFUND_STATE_LABELS } from "./refundState";
import { receivePaymentService } from "@/api/services/receivePaymentService";
import { refundService } from "@/api/services/refundService";
import { deliveryService } from "@/api/services/deliveryService";
import PreviewPhone from "./tradeOrder/components/dialog/previewPhone";
import { TradeOrderDeliveryDialog } from "./tradeOrder/components/deliveryManagement/TradeOrderDeliveryDialog";
import ConfirmPaymentDialog from "./components/payment/confirmPaymentDialog";
import EditPaymentDialog, { type ReceivePaymentUpdatePayload } from "./tradeOrder/components/dialog/editPaymentDialog";
import ProductDetailMapDialog from "../productManagement/ProductDetailMapDialog";
import { useCountdownTimer } from "./tradeOrder/useCountdownTimer";
import "./TradeOrderDetailPage.css";

/** 订单完成凭证沿用 Vue 文件服务，避免弹窗只显示文本而无法上传。 */
async function uploadTradeOrderSettlementFile(file: Blob): Promise<{ id?: number }> {
    const { fileService } = await import("@/api/services/fileService");
    const formData = new FormData();
    formData.append("file", file);
    return fileService.uploadFile(formData);
}

/** 收款确认沿用 Vue 文件服务，确保订单详情收款操作可以上传凭证。 */
async function uploadTradeOrderPaymentVoucher(file: Blob) {
    const formData = new FormData();
    formData.append("file", file);
    return fileService.uploadFile(formData);
}

export interface TradeOrderFinishConfirmVo {
    /** 是否存在完成后会自动取消的待发起或待确认提货。 */
    hasPendingCancelDelivery: boolean;
}

const orderStateLabels: Record<number, string> = {
    0: "--",
    1: "待付款",
    2: "待提货",
    3: "提货中",
    4: "已完成",
    5: "已取消",
    6: "待确认支付",
    7: "拼团中",
    8: "助力中"
};
const orderTypeLabels: Record<number, string> = { 1: "寄售商品", 2: "团购商品", 3: "捡漏商品" };

/** 按订单商品类型返回 Vue 详情页使用的商品详情地址。 */
function getTradeOrderProductDetailPath(productId: number | string | undefined, orderType?: number | string) {
    if (!productId) return undefined;
    const path = {
        1: "/productManagement/consignment/detail",
        2: "/productManagement/groupPurchase/detail",
        3: "/productManagement/bargain/detail"
    }[Number(orderType) as 1 | 2 | 3];
    return path ? `${path}?id=${encodeURIComponent(String(productId))}` : undefined;
}
const orderAuditStateLabels: Record<number, string> = { 0: "待提交", 1: "待审核", 2: "已通过", 3: "未通过" };

/** 对齐旧 Vue 详情页：可取消状态包含待确认支付、提货中和拼团中。 */
export function shouldShowTradeOrderDetailCancel(
    order: { orderType?: number | string; state?: number | string },
    bargainEndFlag?: number | string
): boolean {
    const state = Number(order.state);
    const isUnfinishedBargain =
        Number(order.orderType) === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code &&
        Number(bargainEndFlag) === BARGAIN_END_FLAG.NO.code;
    return !isUnfinishedBargain && [1, 2, 3, 6, 7].includes(state);
}

const waybillStateLabels: Record<number, string> = {
    3: "待发航",
    4: "运输中",
    5: "待卸货",
    8: "已完成",
    10: "待付定金",
    13: "未完成"
};

const supervisionTimeline = [
    { value: 3, label: "待发航", tip: "船舶处于装货阶段，准备发航" },
    { value: 4, label: "运输中", tip: "船舶已发航，正在运输中" },
    { value: 5, label: "待卸货", tip: "船舶已到港，准备卸货" },
    { value: 8, label: "已完成", tip: "已卸货清仓完毕，船运单已完成" }
];

const matchingTimeline = [
    { value: 13, label: "未完成", tip: "已开始船运单履约流程" },
    { value: 8, label: "已完成", tip: "船运单已完成" }
];

function displayValue(value: unknown): string {
    return value === undefined || value === null || value === "" ? "--" : String(value);
}

function displayDate(value: unknown): string {
    // 旧 Vue 详情页统一使用 DATETIME_PICKER_FORMAT，只展示到分钟。
    return value ? dateTimeFormat(String(value)) : "--";
}

interface PaymentCountdownInfo {
    formattedTime: string;
    isEnd: boolean;
    paymentId: string;
    paymentType: number;
    payEndTime: string;
}

/** 对齐旧 Vue：使用截止时间最早的待付款记录计算订单头部支付倒计时。 */
function getPaymentCountdown(receivePayments: ReceivePayment[] | undefined, now: number): PaymentCountdownInfo | null {
    const pendingPayment = (receivePayments || [])
        .filter(
            payment =>
                Number(payment.state) === RECEIVE_STATE.PENDING_PAYMENT.code &&
                Boolean(payment.payEndTime) &&
                Number.isFinite(new Date(payment.payEndTime).getTime())
        )
        .sort((left, right) => new Date(left.payEndTime).getTime() - new Date(right.payEndTime).getTime())[0];
    if (!pendingPayment) return null;

    const remaining = Math.max(0, new Date(pendingPayment.payEndTime).getTime() - now);
    const days = Math.floor(remaining / 86_400_000);
    const hours = Math.floor((remaining % 86_400_000) / 3_600_000);
    const minutes = Math.floor((remaining % 3_600_000) / 60_000);
    const seconds = Math.floor((remaining % 60_000) / 1000);
    const clock = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;

    return {
        formattedTime: days > 0 ? `${days}天${clock}` : clock,
        isEnd: remaining <= 0,
        paymentId: String(pendingPayment.id || ""),
        paymentType: Number(pendingPayment.paymentType),
        payEndTime: String(pendingPayment.payEndTime)
    };
}

function getAddress(delivery: DeliveryVo): string {
    const address = delivery.delivery?.addressInfo;
    if (!address) return "-";
    return [address.province, address.city, address.district, address.detailAddress].filter(Boolean).join("");
}

/** 订单收货地址沿用 Vue orderMessage 的省市区和详细地址拼接规则。 */
function getOrderAddress(order: OrderVo["order"]): string {
    const address = order.addressInfo;
    if (!address) return "--";
    return [
        address.contacts,
        address.cellphone,
        `${areaLib.getFullAreaName(address.province, address.city, address.district)} ${address.detailAddress || ""}`
    ]
        .filter(Boolean)
        .join(" | ");
}

function getTimelineItems(delivery: DeliveryVo) {
    const waybill = delivery.transportOrderShip;
    if (!waybill) return [];
    const state = Number(waybill.state);
    const activities = Number(waybill.serviceType) === 2 ? matchingTimeline : supervisionTimeline;
    const currentIndex = activities.findIndex(item => item.value === state);
    // Vue 在待付定金等未进入履约节点的状态下不展示时间线，保持相同语义。
    if (currentIndex < 0) return [];
    const visibleItems = activities.slice(0, currentIndex + 1);
    return [...visibleItems].reverse();
}

const paymentTypeLabels: Record<number, string> = {
    1: "订单定金",
    2: "排期货款",
    3: "发航货款",
    4: "开仓货款",
    5: "订单货款"
};

/** 发起收款沿用 Vue collectionDialog，仅允许三种订单详情收款费用类型。 */
const collectionPaymentTypeOptions = [
    {
        value: RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.code,
        label: RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.intro
    },
    {
        value: RECEIVE_PAYMENT_TYPE.WAREHOUSE_OPENING_PAYMENT.code,
        label: RECEIVE_PAYMENT_TYPE.WAREHOUSE_OPENING_PAYMENT.intro
    },
    {
        value: RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.code,
        label: RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.intro
    }
];

const paymentStateLabels: Record<number, string> = {
    1: "待支付",
    2: "已支付 (线上)",
    3: "未支付",
    4: "待确认支付",
    5: "已支付 (线下)"
};

function TradeOrderPaymentPanel({
    payments,
    order,
    refunds,
    canDeal,
    onInitiateCollection,
    onAddRefund,
    onPreviewPayment,
    onEditPayment,
    onConfirmPayment,
    onCancelPayment,
    onDeletePayment,
    isPaymentHandler,
    banButton
}: {
    payments: ReceivePayment[];
    order: OrderVo["order"];
    refunds: Refund[];
    canDeal: boolean;
    onInitiateCollection: () => void;
    onAddRefund: () => void;
    onPreviewPayment: (payment: ReceivePayment) => void;
    onEditPayment: (payment: ReceivePayment) => void;
    onConfirmPayment: (payment: ReceivePayment) => void;
    onCancelPayment: (payment: ReceivePayment) => void;
    onDeletePayment: (payment: ReceivePayment) => void;
    isPaymentHandler: boolean;
    banButton: boolean;
}) {
    const paidTotal = payments
        .filter(payment => [2, 5].includes(Number(payment.state)))
        .reduce((total, payment) => total + Number(payment.amount || 0), 0);
    const columns: TableColumnProps<ReceivePayment>[] = [
        {
            key: "id",
            label: "收款编号",
            prop: "id",
            render: ({ row }) => <Link to={`/tradeManage/collection/detail?id=${row.id}`}>{row.id}</Link>
        },
        {
            key: "deliveryId",
            label: "关联提货",
            prop: "deliveryId",
            render: ({ row }) => row.deliveryId || "--"
        },
        {
            key: "paymentType",
            label: "费用类型",
            prop: "paymentType",
            render: ({ row }) => paymentTypeLabels[Number(row.paymentType)] || "--"
        },
        { key: "remark", label: "费用说明", prop: "remark", render: ({ row }) => row.remark || "--" },
        {
            key: "unitPrice",
            label: "单价(元/吨)",
            prop: "unitPrice",
            render: ({ row }) => (row.unitPrice == null ? "--" : fmtMoney(row.unitPrice))
        },
        {
            key: "ton",
            label: "数量(吨)",
            prop: "ton",
            render: ({ row }) => (row.ton == null ? "--" : Number(row.ton).toLocaleString("en-US"))
        },
        {
            key: "amount",
            label: "支付金额(元)",
            prop: "amount",
            render: ({ row }) => (row.amount == null ? "--" : fmtMoney(row.amount))
        },
        {
            key: "paymentInstruction",
            label: "付款说明",
            prop: "paymentInstruction",
            render: ({ row }) => row.paymentInstruction || "--"
        },
        {
            key: "payTime",
            label: "支付时间",
            prop: "payTime",
            render: ({ row }) => displayDate(row.payTime)
        },
        {
            key: "confirmTime",
            label: "确认支付凭证时间",
            prop: "state",
            // 旧 Vue 仅在线下已支付状态展示最后一次确认凭证时间。
            render: ({ row }) => {
                const latestConfirm = row.confirmOperateInfo?.[row.confirmOperateInfo.length - 1];
                return Number(row.state) === 5 ? displayDate(latestConfirm?.confirmTime) : "--";
            }
        },
        {
            key: "state",
            label: "支付状态",
            prop: "state",
            render: ({ row }) => paymentStateLabels[Number(row.state)] || "--"
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            render: ({ row }) => {
                const fileIds = row.paymentFileId || row.customFileId || [];
                const showPreview =
                    (Boolean(row.customFileId?.length) && [3, 4].includes(Number(row.state))) ||
                    (Number(row.state) === 5 && Boolean(row.paymentFileId?.length));
                const canEdit = Number(order.state) !== 4 && Number(order.state) !== 5 && Number(row.state) === 1;
                const canConfirm =
                    isPaymentHandler &&
                    Number(order.state) !== 4 &&
                    Number(order.state) !== 5 &&
                    [1, 4].includes(Number(row.state));
                const canCancel =
                    Number(order.state) !== 4 &&
                    Number(order.state) !== 5 &&
                    ((![1, 5].includes(Number(row.paymentType)) && Number(row.state) === 1) ||
                        [4, 5].includes(Number(row.state)));
                const canDelete = Number(order.state) === 5 && [3, 5].includes(Number(row.state));

                return (
                    <Space size={0} wrap>
                        {showPreview && fileIds.length ? (
                            <Button type="link" onClick={() => onPreviewPayment(row)}>
                                查看凭证
                            </Button>
                        ) : null}
                        {canEdit && isPaymentHandler ? (
                            <Button type="link" onClick={() => onEditPayment(row)}>
                                修改
                            </Button>
                        ) : null}
                        {canConfirm ? (
                            <Button type="link" onClick={() => onConfirmPayment(row)}>
                                确认付款
                            </Button>
                        ) : null}
                        {canCancel ? (
                            <Button type="link" onClick={() => onCancelPayment(row)}>
                                {Number(row.state) === 5 ? "撤回" : "取消"}
                            </Button>
                        ) : null}
                        {canDelete ? (
                            <Button type="link" onClick={() => onDeletePayment(row)}>
                                删除
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section className="section-card mt-4" data-testid="trade-order-payment-panel" id="trade-order-detail-tab-2">
            <div className="trade-order-payment-header">
                <div className="section-title mb-0">收款记录</div>
                <span className="trade-order-payment-total">
                    <span>收款金额总计</span>
                    <strong>{fmtMoney(paidTotal)}元</strong>
                </span>
                {canDeal &&
                Number(order.orderType) !== ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code &&
                Number(order.state) === 3 ? (
                    <Button disabled={banButton} type="primary" onClick={onInitiateCollection}>
                        发起收款
                    </Button>
                ) : null}
                {canDeal && payments.some(payment => [2, 5].includes(Number(payment.state))) && refunds.length === 0 ? (
                    <Button disabled={banButton} type="primary" onClick={onAddRefund}>
                        新增退款
                    </Button>
                ) : null}
            </div>
            <ZhTable<ReceivePayment>
                data={payments}
                columns={normalizeOperationColumns(columns, payments)}
                pagination={false}
                rowKey={payment => String(payment.id)}
            />
        </section>
    );
}

/** 对齐旧 Vue refundHistory，展示退款记录、操作列和订单退款总额。 */
function TradeOrderRefundPanel({
    refunds,
    totalAmount,
    canDeal,
    banButton,
    onAdd,
    onEdit,
    onCancel
}: {
    refunds: Refund[];
    totalAmount?: number;
    canDeal: boolean;
    banButton: boolean;
    onAdd: () => void;
    onEdit: (refund: Refund) => void;
    onCancel: (refund: Refund) => void;
}) {
    const columns: TableColumnProps<Refund>[] = [
        {
            key: "id",
            label: "退款",
            prop: "id",
            render: ({ row }) => <Link to={`/tradeManage/refund/detail?id=${row.id}`}>{row.id}</Link>
        },
        { key: "refundType", label: "退款类型", prop: "refundType", render: ({ row }) => row.refundType || "--" },
        { key: "remark", label: "退款说明", prop: "remark", render: ({ row }) => row.remark || "--" },
        {
            key: "amount",
            label: "退款金额(元)",
            prop: "amount",
            render: ({ row }) => (row.amount == null ? "--" : fmtMoney(row.amount))
        },
        {
            key: "refundTime",
            label: "退款时间",
            prop: "refundTime",
            // Vue 使用 dateTimeFormat，不能把接口返回的 ISO 分隔符 T 直接暴露给用户。
            render: ({ row }) => displayDate(row.refundTime)
        },
        {
            key: "state",
            label: "退款状态",
            prop: "state",
            render: ({ row }) => REFUND_STATE_LABELS[Number(row.state)] || "--"
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            render: ({ row }) => (
                <Space size={0} wrap>
                    {Number(row.state) === 3 ? (
                        <Button disabled={banButton} type="link" onClick={() => onEdit(row)}>
                            修改
                        </Button>
                    ) : null}
                    {[1, 3].includes(Number(row.state)) ? (
                        <Button disabled={banButton} type="link" onClick={() => onCancel(row)}>
                            取消
                        </Button>
                    ) : null}
                </Space>
            )
        }
    ];

    return (
        <section className="section-card mt-4" data-testid="trade-order-refund-panel" id="trade-order-detail-tab-4">
            <div className="trade-order-refund-header">
                <div className="trade-order-refund-header__left">
                    <div className="section-title mb-0">退款记录</div>
                    <div className="trade-order-refund-total">
                        <span>退款金额总计</span>
                        <strong>{fmtMoney(totalAmount)}元</strong>
                    </div>
                </div>
                {canDeal ? (
                    <Button disabled={banButton} type="primary" onClick={onAdd}>
                        新增退款
                    </Button>
                ) : null}
            </div>
            <ZhTable<Refund>
                data={refunds}
                columns={normalizeOperationColumns(columns, refunds)}
                pagination={false}
                rowKey={refund => String(refund.id)}
            />
        </section>
    );
}

/** 按旧 Vue tradeOrder/detail.vue 的状态规则生成订单详情导航项。 */
function getTradeOrderDetailTabs(detail: OrderVo, refunds: Refund[]): Array<{ key: string; label: string }> {
    const state = Number(detail.order.state);
    const tabs: Array<{ key: string; label: string }> = [{ key: "1", label: "订单信息" }];
    if (state !== 8) tabs.push({ key: "2", label: "收款记录" });
    if (refunds.length) tabs.push({ key: "4", label: "退款信息" });
    if ([2, 3, 4, 5].includes(state)) tabs.push({ key: "5", label: "发票信息" });
    // 旧 Vue 在待提货、提货中状态即使暂无提货记录也展示“提货明细”页签；已完成状态才要求存在记录。
    if ([2, 3].includes(state) || (state === 4 && detail.deliveryVos?.length)) {
        tabs.push({ key: "3", label: "提货明细" });
    }
    return tabs;
}

/** 按旧 Vue isShowScheduleCalendar 规则决定提货信息区是否展示；日历另受订单来源限制。 */
function shouldShowTradeOrderDeliveryInfo(detail: OrderVo): boolean {
    const order = detail.order;
    if (Number(order.orderType) === ORDER_TYPE.GROUP_BUYING_PRODUCTS.code) {
        return Number(detail.productGroupPurchase?.state) === GROUP_PURCHASE_STATE.SUCCESS.code;
    }
    if (Number(order.state) !== 5) return [2, 3, 4].includes(Number(order.state));
    // 取消订单沿用 Vue 的 beforeCancelState 判断，避免误用完成流程字段而丢失排期区。
    return Number(order.beforeCancelState) > 1;
}

/** 按旧 Vue isShowDeliveryDetail 规则决定是否显示提货明细二级区块。 */
function shouldShowTradeOrderDeliveryDetail(detail: OrderVo): boolean {
    const order = detail.order;
    const state = Number(order.state);
    // 旧版已完成订单只有存在提货记录时才渲染提货明细；避免空表占据滚动区域。
    if (state === 3) return true;
    if (state === 4) return Boolean(detail.deliveryVos?.length);
    if (state === 5) return [3, 4].includes(Number(order.beforeCancelState));
    return state === 2 && [1, 2].includes(Number(order.orderType));
}

/** 旧 Vue 的 deliveryInfo：排期日历前固定展示订单提货周期、吨数与提货地点。 */
function TradeOrderDeliveryInfoPanel({ detail }: { detail: OrderVo }) {
    const order = detail.order;
    const address = order.deliveryAddressInfo;
    // 地区字典异步加载后重新计算，和旧版一样展示省、市、区及详细地址。
    useSyncExternalStore(areaLib.subscribe, () => areaLib.version);
    const fullAddress = address
        ? `${areaLib.getFullAreaName(address.province, address.city, address.district)}${address.detailAddress || ""}` ||
          "--"
        : "--";
    const period =
        order.deliveryStartTime && order.deliveryEndTime
            ? `${displayDate(order.deliveryStartTime).slice(0, 10)} 至 ${displayDate(order.deliveryEndTime).slice(0, 10)}`
            : "--";

    return (
        <section className="section-card mt-4 trade-order-delivery-info-panel">
            <div className="section-title mb-5">提货信息</div>
            <ZhGrid cols={3} gap={[20, 20]}>
                <ZhBaseItem colon={false} label="提货周期" value={period} />
                <ZhBaseItem
                    colon={false}
                    label="可提货吨数"
                    value={order.ton ? `${Number(order.ton).toLocaleString("en-US")} 吨` : "--"}
                />
                <ZhBaseItem colon={false} label="提货地点">
                    {address?.geo ? (
                        <ProductDetailMapDialog
                            inline
                            location={{
                                provinceCode: address.province,
                                cityCode: address.city,
                                regionCode: address.district,
                                address: address.detailAddress,
                                geo: address.geo
                            }}
                        />
                    ) : (
                        <span>{fullAddress}</span>
                    )}
                </ZhBaseItem>
            </ZhGrid>
        </section>
    );
}

const deliveryStateLabels: Record<number, string> = {
    1: "待确认",
    2: "已确认",
    3: "已提货",
    4: "已取消",
    6: "待发起"
};

const deliveryTypeLabels: Record<number, string> = { 1: "排期产生", 2: "手动新增" };

/** 寄售订单沿用旧 Vue 的提货明细表；团购/捡漏继续使用履约详情面板。 */
function TradeOrderDeliveryListPanel({
    deliveries,
    orderId,
    orderOrigin,
    orderState,
    canDeal,
    banButton,
    onAddDelivery,
    onRefreshDeliveries,
    onOpenDelivery,
    onInitiateDelivery,
    onCancelDelivery,
    onDeleteDelivery
}: {
    deliveries: DeliveryVo[];
    orderId: string;
    orderOrigin: number;
    orderState: number;
    canDeal: boolean;
    banButton: boolean;
    onAddDelivery: () => void;
    onRefreshDeliveries: () => void;
    onOpenDelivery: (delivery: DeliveryVo) => void;
    onInitiateDelivery: (delivery: DeliveryVo) => void;
    onCancelDelivery: (delivery: DeliveryVo) => void;
    onDeleteDelivery: (delivery: DeliveryVo) => void;
}) {
    // 对齐旧 Vue 宽表：首列和操作列在横向滚动时分别固定在左右两侧。
    const columns: TableColumnProps<DeliveryVo>[] = [
        {
            key: "id",
            label: "提货编号",
            prop: "delivery.id",
            // Ant 固定列需要明确宽度；仅设 minWidth 会把列拉伸，导致横向滚动范围偏离旧版。
            width: 180,
            fixed: "left",
            render: ({ row }) => (
                <Link
                    to={`/tradeManage/delivery/detail?id=${encodeURIComponent(String(row.delivery?.id || ""))}&orderId=${encodeURIComponent(String(orderId))}&orderOrigin=${encodeURIComponent(String(orderOrigin))}`}
                >
                    {row.delivery?.id || "--"}
                </Link>
            )
        },
        {
            key: "scheduleDate",
            label: "提货日期",
            prop: "delivery.scheduleDate",
            width: 190,
            render: ({ row }) => displayDate(row.delivery?.scheduleDate).slice(0, 10)
        },
        {
            key: "settlementAmount",
            label: "结算金额",
            prop: "delivery.settlementAmount",
            width: 170,
            align: "right",
            render: ({ row }) => displayValue(row.delivery?.settlementAmount)
        },
        {
            key: "estimatedTon",
            label: "提货吨位（吨）",
            prop: "delivery.estimatedTon",
            width: 170,
            align: "right",
            render: ({ row }) => displayValue(row.delivery?.estimatedTon)
        },
        {
            key: "settlementUnitAmount",
            label: "结算单价（元/吨）",
            prop: "delivery.settlementUnitAmount",
            width: 180,
            align: "right",
            render: ({ row }) => displayValue(row.delivery?.settlementUnitAmount)
        },
        {
            key: "state",
            label: "状态",
            prop: "delivery.state",
            width: 170,
            render: ({ row }) => deliveryStateLabels[Number(row.delivery?.state)] || "--"
        },
        {
            key: "scheduleId",
            label: "关联排期",
            prop: "delivery.scheduleId",
            width: 180,
            render: ({ row }) => row.delivery?.scheduleId || "--"
        },
        {
            key: "type",
            label: "类型",
            prop: "delivery.type",
            width: 170,
            render: ({ row }) => deliveryTypeLabels[Number(row.delivery?.type)] || "--"
        },
        {
            key: "createdTime",
            label: "创建时间",
            prop: "delivery.createdTime",
            width: 190,
            render: ({ row }) => displayDate(row.delivery?.createdTime)
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            width: 240,
            fixed: "right",
            render: ({ row }) => {
                const state = Number(row.delivery?.state);
                const canOperate =
                    canDeal && !banButton && Number(row.delivery?.newDataFlag) === 1 && ![4, 5].includes(orderState);
                return (
                    <Space size={0} wrap>
                        {canOperate && [6, 2, 3].includes(state) ? (
                            <Button type="link" onClick={() => onOpenDelivery(row)}>
                                修改
                            </Button>
                        ) : null}
                        {canOperate && [1, 6].includes(state) ? (
                            <Button type="link" onClick={() => onCancelDelivery(row)}>
                                取消提货
                            </Button>
                        ) : null}
                        {canOperate && state === 6 ? (
                            <Button type="link" onClick={() => onInitiateDelivery(row)}>
                                发起提货
                            </Button>
                        ) : null}
                        {canOperate && state === 4 ? (
                            <Button type="link" onClick={() => onDeleteDelivery(row)}>
                                删除
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];
    const normalizedColumns = normalizeOperationColumns(columns, deliveries);
    // 用当前操作列的动态宽度计算整表宽度，覆盖历史 1820px 常量并保持不同状态按钮不溢出。
    const deliveryTableWidth = normalizedColumns.reduce(
        (total, column) => total + Number(column.width ?? column.minWidth ?? 0),
        0
    );

    return (
        <section
            className="section-card mt-4 trade-order-delivery-detail-panel"
            style={
                {
                    "--trade-order-delivery-table-width": `${deliveryTableWidth}px`
                } as CSSProperties
            }
        >
            <div className="section-title mb-0">提货明细</div>
            <div className="trade-order-delivery-toolbar">
                <Button type="primary" onClick={onAddDelivery}>
                    新增提货
                </Button>
                <Input placeholder="请输入提货编号" />
                <Input placeholder="请输入排期编号" />
                <Select
                    placeholder="请选择提货状态"
                    options={Object.entries(deliveryStateLabels).map(([value, label]) => ({ value, label }))}
                />
                <Select
                    placeholder="请选择类型"
                    options={Object.entries(deliveryTypeLabels).map(([value, label]) => ({ value, label }))}
                />
                <Button className="trade-order-delivery-toolbar__search" type="primary" onClick={onRefreshDeliveries}>
                    <i aria-hidden="true" className="iconfont icon-Frame-15" />
                    搜索
                </Button>
                <Button
                    className="trade-order-delivery-toolbar__refresh"
                    aria-label="刷新提货明细"
                    onClick={onRefreshDeliveries}
                >
                    <i aria-hidden="true" className="iconfont icon-Frame8" />
                </Button>
            </div>
            <ZhTable<DeliveryVo>
                data={deliveries}
                columns={normalizedColumns}
                pagination={false}
                rowKey={row => String(row.delivery?.id)}
            />
            <div className="trade-order-delivery-pagination">
                <span>共{deliveries.length}条数据</span>
                <div className="trade-order-delivery-pagination__controls">
                    <Select
                        className="trade-order-delivery-pagination__page-size"
                        value="10"
                        options={[{ value: "10", label: "10条/页" }]}
                    />
                    <Button aria-label="上一页" disabled>
                        ‹
                    </Button>
                    <span className="is-current">1</span>
                    <Button aria-label="下一页" disabled>
                        ›
                    </Button>
                    <span className="trade-order-delivery-pagination__jump-label">前往</span>
                    <Input className="trade-order-delivery-pagination__jump-input" readOnly value="1" />
                    <span>页</span>
                </div>
            </div>
        </section>
    );
}

/** 对照 Vue deliveryMessage/logisticsTracking/proveMaterial，展示单条提货的履约闭环。 */
function DeliveryFulfillmentPanel({ deliveries }: { deliveries: DeliveryVo[] }) {
    if (!deliveries.length) {
        return (
            <Card className="mt-4" title="提货详情">
                <Empty description="暂无提货履约数据" />
            </Card>
        );
    }

    return (
        <Card className="mt-4" title="提货详情">
            {deliveries.map((delivery, index) => {
                const waybill = delivery.transportOrderShip;
                const details = delivery.transportOrderDetailsShip;
                const timelineItems = getTimelineItems(delivery);
                const waybillState = waybill ? waybillStateLabels[Number(waybill.state)] || "未知状态" : "未关联运单";
                const evidence = delivery.delivery?.evidenceMaterialInfo;
                const evidenceItems = [
                    ["吨位证明", evidence?.tonProveFileId],
                    ["砂石来源证明", evidence?.sandSourceProofFileId],
                    ["到装货港图片/视频", evidence?.loadingPortFileId],
                    ["清仓图片/视频", evidence?.clearanceSaleFileId]
                ];

                return (
                    <section key={delivery.delivery?.id || index} aria-label={`提货履约-${index + 1}`}>
                        {index > 0 ? <div className="my-4 border-t border-gray-200" /> : null}
                        <Typography.Title className="mb-3 mt-0" level={5}>
                            第 {index + 1} 条提货履约
                        </Typography.Title>
                        {/* 使用 zhihao-ui-react 网格承载履约信息，保持 Vue 字段顺序与显示口径。 */}
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="履约状态">
                                <StatusTag tone={waybill ? "primary" : "info"}>{waybillState}</StatusTag>
                            </ZhBaseItem>
                            <ZhBaseItem label="提货日期" value={displayDate(delivery.delivery?.scheduleDate)} />
                            <ZhBaseItem
                                label="提货吨数"
                                value={`${displayValue(delivery.delivery?.estimatedTon)} 吨`}
                            />
                            <ZhBaseItem
                                label="结算单价"
                                value={`${displayValue(delivery.delivery?.settlementUnitAmount)} 元/吨`}
                            />
                            <ZhBaseItem
                                label="关联运单"
                                value={waybill ? `${displayValue(waybill.id)}（${waybillState}）` : "-"}
                            />
                            <ZhBaseItem
                                label="船舶"
                                value={displayValue(
                                    delivery.delivery?.shipInfo?.cnname || delivery.delivery?.shipInfo?.name
                                )}
                            />
                            <ZhBaseItem label="收货人" value={displayValue(delivery.delivery?.addressInfo?.contacts)} />
                            <ZhBaseItem
                                label="收货联系方式"
                                value={displayValue(delivery.delivery?.addressInfo?.cellphone)}
                            />
                            <ZhBaseItem label="收货地址" span={4} value={getAddress(delivery)} />
                            <ZhBaseItem label="装货时间" value={displayDate(details?.deliveryInfo?.loadingDate)} />
                            <ZhBaseItem
                                label="集港货物吨位"
                                value={`${displayValue(details?.deliveryInfo?.portCargoTonnage)} 吨`}
                            />
                            <ZhBaseItem label="到港时间" value={displayDate(details?.captainArrivalTime)} />
                            <ZhBaseItem
                                label="卸货完成时间"
                                value={displayDate(details?.unloadingInfo?.unloadingCompletionTime)}
                            />
                        </ZhGrid>

                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Card size="small" title="物流追踪">
                                {waybill ? (
                                    <Timeline
                                        items={timelineItems.map(item => ({
                                            color: item.value === Number(waybill.state) ? "blue" : "gray",
                                            content: (
                                                <div>
                                                    <div>{item.label}</div>
                                                    <Typography.Text type="secondary">{item.tip}</Typography.Text>
                                                </div>
                                            )
                                        }))}
                                    />
                                ) : (
                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂未关联运单数据" />
                                )}
                            </Card>
                            <Card size="small" title="佐证材料">
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    {evidenceItems.map(([label, fileId]) => (
                                        <div key={label} className="rounded border border-dashed border-gray-300 p-2">
                                            <Typography.Text strong>{label}</Typography.Text>
                                            <div className="text-gray-500">
                                                {fileId ? `文件 ${fileId}` : "暂无材料"}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-2 rounded border border-dashed border-gray-300 p-2">
                                    <Typography.Text strong>地图/视频轨迹</Typography.Text>
                                    <div className="text-gray-500">
                                        {waybill ? "轨迹数据由运单服务提供，当前页面保留接口展示位" : "暂无轨迹数据"}
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </section>
                );
            })}
        </Card>
    );
}

/** 详情备注中的文件入口按旧 Vue BaseItem 语义显示查看/下载，而不是直接暴露 fileId。 */
function TradeOrderRemarkFileLink({ fileId }: { fileId: number }) {
    const preview = async () => {
        const popup = window.open("about:blank", "_blank", "noopener,noreferrer");
        try {
            const file = await fileService.getFileById(fileId);
            if (popup && file.path) popup.location.href = file.path;
            else if (file.path) window.open(file.path, "_blank", "noopener,noreferrer");
        } catch {
            popup?.close();
        }
    };

    return (
        <Button
            aria-label="查看/下载备注附件"
            className="trade-order-remark-file-link"
            type="link"
            onClick={() => void preview()}
        >
            查看/下载
        </Button>
    );
}

/** 对齐旧 Vue orderRemark：展示备注文件入口以及修改、删除操作。 */
function TradeOrderRemarksPanel({
    remarks,
    onEdit,
    onDelete
}: {
    remarks: OrderRemark[];
    onEdit: (remark: OrderRemark) => void;
    onDelete: (id: number) => void;
}) {
    if (!remarks.length) return null;

    return (
        <section className="section-card mt-4 trade-order-remarks" data-testid="trade-order-remarks">
            <div className="section-title mb-5">订单备注</div>
            <div className="flex flex-col gap-4">
                {remarks.map(remark => (
                    <div key={remark.id} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                        <ZhGrid cols={8} gap={[20, 20]}>
                            <ZhBaseItem label="备注人">
                                <div>{displayValue(remark.handleName)}</div>
                                <div className="text-xs text-gray-500">{displayDate(remark.createdTime)}</div>
                            </ZhBaseItem>
                            <ZhBaseItem label="文件凭证">
                                <TradeOrderRemarkFileLink fileId={remark.fileId} />
                            </ZhBaseItem>
                            <ZhBaseItem label="备注说明" span={6} value={displayValue(remark.remark)} />
                        </ZhGrid>
                        <div className="trade-order-remark-actions">
                            <Button type="link" onClick={() => onEdit(remark)}>
                                修改
                            </Button>
                            <Button type="link" onClick={() => onDelete(Number(remark.id))}>
                                删除
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

export interface TradeOrderDetailPageProps {
    addOrderRemark?: (params: { orderId: number | string; fileId: number; remark?: string }) => Promise<unknown>;
    updateOrderRemark?: (
        id: number,
        params: { orderId: number | string; fileId: number; remark?: string }
    ) => Promise<unknown>;
    removeOrderRemark?: (id: number) => Promise<unknown>;
    assignOrder?: (params: { orderId: number | string; handlerId: string }) => Promise<unknown>;
    loadCommissioners?: () => Promise<ServiceSpecialVo[]>;
    loadDetail?: (id: number | string) => Promise<OrderVo>;
    loadRefunds?: (query: { page: number; size: number; orderId: string }) => Promise<TradeOrderRefundListResult>;
    loadDeliveries?: (query: { page: number; size: number; orderId: string }) => Promise<{ list?: DeliveryVo[] }>;
    cancelOrder?: (id: number | string) => Promise<unknown>;
    deleteOrder?: (id: number | string) => Promise<unknown>;
    finishOrder?: (id: number | string, form: OrderFinishForm) => Promise<unknown>;
    loadFinishConfirm?: (id: number | string) => Promise<TradeOrderFinishConfirmVo>;
    updatePickedUpTon?: (id: number | string, form: OrderFinishForm) => Promise<unknown>;
    createPayment?: (form: ReceivePaymentForm) => Promise<unknown>;
    createRefund?: (form: RefundForm) => Promise<unknown>;
    loadInvoices?: (query: {
        page: number;
        size: number;
        orderId: string;
    }) => Promise<{ list?: Invoice[]; total?: number }>;
    permissions?: string[];
}

export interface TradeOrderRefundListResult {
    list?: Refund[];
    total?: number;
}

/** 复用旧 Vue deliveryManagement 的订单提货分页接口。 */
export async function loadTradeOrderDeliveries(query: {
    page: number;
    size: number;
    orderId: string;
}): Promise<{ list?: DeliveryVo[] }> {
    return deliveryService.getPaging(query);
}

/** 复用 Vue 订单详情接口，展示详情页的基础订单信息。 */
export async function loadTradeOrderDetail(id: number | string): Promise<OrderVo> {
    const response = await request.service.get<OrderVo>(`/admin/order/vo/${id}`);
    return response.data;
}

/** 复用旧 Vue 订单详情的专员查询接口。 */
export async function loadTradeOrderDetailCommissioners(): Promise<ServiceSpecialVo[]> {
    const response = await request.service.get<ServiceSpecialVo[]>("/admin/order/find/specials");
    return response.data;
}

/** 复用旧 Vue 订单详情的专员指派接口。 */
export async function assignTradeOrderDetail(params: { orderId: number | string; handlerId: string }): Promise<void> {
    await request.service.put(`/admin/order/assign/${params.orderId}/${params.handlerId}`);
}

/** 复用 Vue 订单详情的订单备注接口，保持附件 ID 和备注字段结构。 */
export async function addTradeOrderDetailRemark(params: {
    orderId: number | string;
    fileId: number;
    remark?: string;
}): Promise<void> {
    await request.service.post("/admin/order-remark", params);
}

/** 复用旧 Vue 订单备注编辑接口。 */
export async function updateTradeOrderDetailRemark(
    id: number,
    params: { orderId: number | string; fileId: number; remark?: string }
): Promise<void> {
    await orderRemarkService.updateOrderRemark(id, {
        orderId: Number(params.orderId),
        fileId: params.fileId,
        ...(params.remark ? { remark: params.remark } : {})
    });
}

/** 复用旧 Vue 订单备注删除接口。 */
export async function deleteTradeOrderDetailRemark(id: number): Promise<void> {
    await orderRemarkService.deleteOrderRemark(id);
}

/** 复用 Vue 退款记录分页接口契约，仅读取详情页前五条退款记录。 */
export async function loadTradeOrderRefunds(query: {
    page: number;
    size: number;
    orderId: string;
}): Promise<TradeOrderRefundListResult> {
    const response = await request.service.get<TradeOrderRefundListResult>("/admin/refund/paging", {
        params: query
    });
    return response.data;
}

/** 复用旧 Vue 订单详情关联发票分页接口。 */
export async function loadTradeOrderInvoices(query: {
    page: number;
    size: number;
    orderId: string;
}): Promise<{ list?: Invoice[]; total?: number }> {
    const response = await request.service.get<{ list?: Invoice[]; total?: number }>("/admin/invoice/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 订单取消接口契约。 */
export async function cancelTradeOrderDetail(id: number | string) {
    const response = await request.service.put(`/admin/order/cancel/${id}`);
    return response.data;
}

/** 复用旧 Vue 订单删除接口，成功后由详情页返回列表。 */
export async function deleteTradeOrderDetail(id: number | string): Promise<void> {
    await request.service.delete(`/admin/order/${id}`);
}

/** 复用旧 Vue 订单详情完成接口，保持完成表单字段结构。 */
export async function finishTradeOrderDetail(id: number | string, form: OrderFinishForm): Promise<void> {
    await request.service.put(`/admin/order/finish/${id}`, form);
}

/** 复用旧 Vue 已完成订单修改提货吨数接口。 */
export async function updateTradeOrderPickedUpTon(id: number | string, form: OrderFinishForm): Promise<void> {
    await request.service.put(`/admin/order/update/ton/${id}`, form);
}

/** 复用旧 Vue 完成前确认接口，决定提货自动取消提示文案。 */
export async function loadTradeOrderFinishConfirm(id: number | string): Promise<TradeOrderFinishConfirmVo> {
    const response = await request.service.get<TradeOrderFinishConfirmVo>(`/admin/order/finish-confirm/${id}`);
    return response.data;
}

/** 将 Vue 交易订单详情返回的枚举字段统一为详情文案使用的数字。 */
function normalizeTradeOrderDetail(result: OrderVo): OrderVo {
    return {
        ...result,
        order: {
            ...result.order,
            orderType: Number(result.order.orderType),
            state: Number(result.order.state),
            beforeFinishState: Number(result.order.beforeFinishState),
            orderOrigin: Number(result.order.orderOrigin),
            auditState: Number(result.order.auditState),
            payType: Number(result.order.payType)
        },
        bargainEndFlag: Number(result.bargainEndFlag)
    };
}

/** 交易订单详情基础页，完整复用 Vue 详情数据、字段、履约区和操作流程。 */
export function TradeOrderDetailPage({
    addOrderRemark = addTradeOrderDetailRemark,
    updateOrderRemark = updateTradeOrderDetailRemark,
    removeOrderRemark = deleteTradeOrderDetailRemark,
    assignOrder = assignTradeOrderDetail,
    loadCommissioners = loadTradeOrderDetailCommissioners,
    loadDetail = loadTradeOrderDetail,
    loadRefunds = loadTradeOrderRefunds,
    loadDeliveries = loadTradeOrderDeliveries,
    cancelOrder = cancelTradeOrderDetail,
    deleteOrder = deleteTradeOrderDetail,
    finishOrder = finishTradeOrderDetail,
    loadFinishConfirm = loadTradeOrderFinishConfirm,
    updatePickedUpTon = updateTradeOrderPickedUpTon,
    createPayment = (form: ReceivePaymentForm) => receivePaymentService.createPayment(form),
    createRefund = (form: RefundForm) => refundService.createInDialog(form),
    loadInvoices = loadTradeOrderInvoices,
    permissions
}: TradeOrderDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const orderId = new URLSearchParams(location.search).get("id") || "";
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 未传入 permissions 时保留现有页面测试和本地直达页面的默认可见行为；正式路由可传入权限快照。
    const currentPermissions = permissions ?? [
        "ROLE_TRADE_ORDER_MANAGE",
        "ROLE_TRADE_ORDER_DEAL",
        "ROLE_TRADE_ORDER_DELETE",
        "ROLE_TRADE_ORDER_CANCEL",
        ...storePermissions
    ];
    const [detail, setDetail] = useState<OrderVo | null>(null);
    const [refunds, setRefunds] = useState<Refund[]>([]);
    const [deliveryRows, setDeliveryRows] = useState<DeliveryVo[]>([]);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelSubmitting, setCancelSubmitting] = useState(false);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [finishOpen, setFinishOpen] = useState(false);
    const [finishSubmitting, setFinishSubmitting] = useState(false);
    const [finishError, setFinishError] = useState<string | null>(null);
    const [editPickedUpTonOpen, setEditPickedUpTonOpen] = useState(false);
    const [editPickedUpTonSubmitting, setEditPickedUpTonSubmitting] = useState(false);
    const [editPickedUpTonError, setEditPickedUpTonError] = useState<string | null>(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [commissioners, setCommissioners] = useState<ServiceSpecialVo[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState("");
    const [assignError, setAssignError] = useState<string | null>(null);
    const [remarkOpen, setRemarkOpen] = useState(false);
    const [remarkEditingId, setRemarkEditingId] = useState<number | null>(null);
    const [remarkFileId, setRemarkFileId] = useState("");
    const [remarkFileName, setRemarkFileName] = useState("");
    const [remarkFilePath, setRemarkFilePath] = useState("");
    const [remarkContent, setRemarkContent] = useState("");
    const [remarkError, setRemarkError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState("1");
    const [operationLogRefreshToken, setOperationLogRefreshToken] = useState(0);
    const [snapshotOpen, setSnapshotOpen] = useState(false);
    const [collectionOpen, setCollectionOpen] = useState(false);
    const [collectionType, setCollectionType] = useState<number | undefined>();
    const [collectionDeliveryId, setCollectionDeliveryId] = useState<string | undefined>();
    const [collectionDeliveryOptions, setCollectionDeliveryOptions] = useState<DeliveryVo[]>([]);
    const [collectionTon, setCollectionTon] = useState<number>();
    const [collectionTonProofFileId, setCollectionTonProofFileId] = useState<number>();
    const [collectionAmount, setCollectionAmount] = useState<number>();
    const [collectionRemark, setCollectionRemark] = useState("");
    const [refundOpen, setRefundOpen] = useState(false);
    const [refundTargetId, setRefundTargetId] = useState<string>();
    const [refundType, setRefundType] = useState("");
    const [refundAmount, setRefundAmount] = useState<number>();
    const [refundRemark, setRefundRemark] = useState("");
    const [paymentActionError, setPaymentActionError] = useState<string | null>(null);
    const [paymentActionSubmitting, setPaymentActionSubmitting] = useState(false);
    const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
    const [editingDelivery, setEditingDelivery] = useState<DeliveryVo>();
    const [paymentTarget, setPaymentTarget] = useState<ReceivePayment | null>(null);
    const [editPaymentOpen, setEditPaymentOpen] = useState(false);
    const [confirmPaymentOpen, setConfirmPaymentOpen] = useState(false);
    const { restTimeKey } = useCountdownTimer();
    const refreshedExpiredPaymentRef = useRef("");

    /** Vue collectionDialog 打开时查询可关联的已确认/已提货单据。 */
    useEffect(() => {
        if (
            !collectionOpen ||
            !detail?.order?.id ||
            Number(detail.order.orderType) === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code
        ) {
            return;
        }
        let disposed = false;
        void deliveryService
            .getSelector({
                orderId: String(detail.order.id),
                states: [DELIVERY_STATE.CONFIRMED.code, DELIVERY_STATE.PICKED_UP.code]
            })
            .then(options => {
                if (!disposed) setCollectionDeliveryOptions(options || []);
            })
            .catch(() => {
                if (!disposed) setCollectionDeliveryOptions([]);
            });
        return () => {
            disposed = true;
        };
    }, [collectionOpen, detail?.order?.id, detail?.order?.orderType]);

    /** 旧 Vue deliveryManagement 独立分页查询提货记录，不能依赖订单详情中的 deliveryVos。 */
    useEffect(() => {
        if (!detail?.order?.id) {
            setDeliveryRows([]);
            return;
        }
        let disposed = false;
        void loadDeliveries({ page: 1, size: 10, orderId: String(detail.order.id) })
            .then(result => {
                if (!disposed) setDeliveryRows(result.list || []);
            })
            .catch(() => {
                if (!disposed) setDeliveryRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [detail?.order?.id, loadDeliveries]);

    /** 删除已完成/已取消订单并返回上一页，保持旧 Vue onDelete 行为。 */
    const handleDeleteOrder = async () => {
        if (!order) return;
        try {
            const confirmed = await confirmDialog("是否删除订单，删除后此订单不在展示", "提示");
            if (!confirmed) return;
            await deleteOrder(order.id);
            message.success("删除订单成功");
            navigate(-1);
        } catch (error) {
            showRequestError(error, "删除订单失败");
        }
    };

    /** 提交完成表单并重新读取详情，沿用 Vue complete-order-dialog 的刷新语义。 */
    const submitFinishOrder = async (form: OrderFinishForm) => {
        if (!order) return;
        setFinishSubmitting(true);
        setFinishError(null);
        try {
            await finishOrder(order.id, form);
            message.success("完成订单成功");
            setFinishOpen(false);
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setFinishError("完成订单失败");
        } finally {
            setFinishSubmitting(false);
        }
    };

    /** 按旧 Vue 状态规则打开完成弹窗或执行提货中的二次确认。 */
    const openFinishOrder = async () => {
        if (!order || ![2, 3].includes(order.state)) return;
        setFinishError(null);
        if (order.state === 2) {
            setFinishOpen(true);
            return;
        }
        try {
            const confirmInfo = await loadFinishConfirm(order.id);
            const confirmMessage = confirmInfo.hasPendingCancelDelivery
                ? "是否完成，完成后待发起和待确认的提货将自动取消且不能再提货。"
                : "是否完成，完成后不能再提货";
            const confirmed = await confirmDialog(confirmMessage, "提示", {
                className: "trade-order-finish-confirm-box",
                confirmButtonText: "确认",
                cancelButtonText: "取消"
            });
            if (confirmed) await submitFinishOrder({});
        } catch {
            setFinishError("完成订单失败");
        }
    };

    /** 修改待提货直接完成订单的提货吨数和结算凭证，并刷新详情。 */
    const submitPickedUpTon = async (form: OrderFinishForm) => {
        if (!order) return;
        setEditPickedUpTonSubmitting(true);
        setEditPickedUpTonError(null);
        try {
            await updatePickedUpTon(order.id, form);
            message.success("修改提货吨数成功");
            setEditPickedUpTonOpen(false);
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setEditPickedUpTonError("修改提货吨数失败");
        } finally {
            setEditPickedUpTonSubmitting(false);
        }
    };

    /** 取消订单成功后重新读取详情，保持 Vue 页面刷新语义。 */
    const handleCancelOrder = () => {
        if (!order) return;
        setCancelError(null);
        setCancelOpen(true);
    };

    /** 提交详情页取消请求，成功后按 Vue 行为重新拉取订单详情。 */
    const submitCancelOrder = async () => {
        if (!order) return;
        setCancelSubmitting(true);
        setCancelError(null);
        try {
            await cancelOrder(order.id);
            message.success("取消订单成功");
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
            setCancelOpen(false);
        } catch {
            setCancelError("交易订单取消失败");
        } finally {
            setCancelSubmitting(false);
        }
    };

    /** 打开指派弹窗并按旧 Vue 约定加载可选专员。 */
    const openAssignDialog = async () => {
        if (!order) return;
        setAssignOpen(true);
        setAssignError(null);
        setSelectedHandlerId(order.handlerId == null ? "" : String(order.handlerId));
        try {
            setCommissioners((await loadCommissioners()) || []);
        } catch {
            setCommissioners([]);
            setAssignError("订单专员查询失败");
        }
    };

    /** 提交指派并重新读取详情，保持旧 Vue 操作后的刷新语义。 */
    const submitOrderAssign = async () => {
        if (!order?.id || !selectedHandlerId) {
            setAssignError("请选择订单专员");
            return;
        }
        try {
            await assignOrder({ orderId: order.id, handlerId: selectedHandlerId });
            setAssignOpen(false);
            setAssignError(null);
            message.success("指派成功");
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setAssignError("订单专员指派失败");
        }
    };

    /** 打开订单备注弹窗，沿用旧 Vue 的空表单初始化行为。 */
    const openOrderRemark = (remark?: OrderRemark) => {
        setRemarkEditingId(remark?.id == null ? null : Number(remark.id));
        setRemarkFileId(remark?.fileId == null ? "" : String(remark.fileId));
        setRemarkFileName(remark?.fileId == null ? "" : String(remark.fileId));
        setRemarkFilePath("");
        setRemarkContent(remark?.remark || "");
        setRemarkError(null);
        setRemarkOpen(true);
    };

    /** 提交订单备注并刷新详情，附件 ID 为旧 Vue 接口必填字段。 */
    const submitOrderRemark = async () => {
        if (!order?.id || !remarkFileId.trim() || !Number.isFinite(Number(remarkFileId))) {
            setRemarkError("请上传附件");
            return;
        }
        try {
            const normalizedRemark = remarkContent.trim();
            const payload = {
                orderId: Number(order.id),
                fileId: Number(remarkFileId),
                ...(normalizedRemark ? { remark: normalizedRemark } : {})
            };
            if (remarkEditingId != null) await updateOrderRemark(remarkEditingId, payload);
            else await addOrderRemark(payload);
            setRemarkOpen(false);
            setRemarkEditingId(null);
            setRemarkError(null);
            message.success("备注成功");
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setRemarkError("订单备注保存失败");
        }
    };

    /** 使用旧 Vue 文件服务上传备注附件，并将返回文件 ID 回填到表单。 */
    const uploadOrderRemarkFile = async (options: UploadRequestOptions) => {
        const file = options.file as File;
        const formData = new FormData();
        formData.append("file", file);
        try {
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setRemarkFileId(String(uploadedFile.id));
            setRemarkFileName(file.name);
            setRemarkFilePath(uploadedFile.path || "");
            setRemarkError(null);
            options.onSuccess(uploadedFile);
        } catch (error) {
            setRemarkError("订单备注附件上传失败");
            options.onError(error instanceof Error ? error : new Error("订单备注附件上传失败"));
        }
    };

    /** 打开刚上传的备注附件，保持旧 Vue SingleUpload 的查看/下载语义。 */
    const previewOrderRemarkFile = async () => {
        const popup = window.open("about:blank", "_blank", "noopener,noreferrer");
        try {
            const file = remarkFilePath
                ? { path: remarkFilePath }
                : await fileService.getFileById(Number(remarkFileId));
            if (popup && file.path) popup.location.href = file.path;
            else if (file.path) window.open(file.path, "_blank", "noopener,noreferrer");
        } catch {
            popup?.close();
        }
    };

    /** 删除当前弹窗附件并清空提交所需的文件 ID。 */
    const removeOrderRemarkFile = () => {
        setRemarkFileId("");
        setRemarkFileName("");
        setRemarkFilePath("");
    };

    /** 删除详情中的历史备注，确认后重新读取详情和操作日志。 */
    const handleDeleteOrderRemark = async (id: number) => {
        if (!(await confirmDialog("是否删除订单备注，删除后此订单备注不再展示", "是否删除"))) return;
        try {
            await removeOrderRemark(id);
            if (order) setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setRemarkError("订单备注删除失败");
        }
    };

    /** 提交详情页发起收款，保持 Vue 收款表单的最小必填字段和刷新语义。 */
    const submitCollection = async () => {
        if (!order?.id || collectionType == null) {
            setPaymentActionError("请选择费用类型");
            return;
        }
        if (!collectionAmount || collectionAmount <= 0) {
            setPaymentActionError("请输入支付金额");
            return;
        }
        if (collectionType === RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.code && (!collectionTon || collectionTon <= 0)) {
            setPaymentActionError("请输入发航吨位");
            return;
        }
        setPaymentActionSubmitting(true);
        setPaymentActionError(null);
        try {
            await createPayment({
                orderId: String(order.id),
                paymentType: collectionType,
                amount: collectionAmount,
                ...(collectionDeliveryId ? { deliveryId: collectionDeliveryId } : {}),
                ...(collectionTon ? { ton: collectionTon } : {}),
                ...(collectionTonProofFileId ? { tonProveFileId: collectionTonProofFileId } : {}),
                ...(collectionRemark.trim() ? { remark: collectionRemark.trim() } : {})
            });
            message.success("发起收款成功");
            setCollectionOpen(false);
            setCollectionType(undefined);
            setCollectionDeliveryId(undefined);
            setCollectionDeliveryOptions([]);
            setCollectionAmount(undefined);
            setCollectionTon(undefined);
            setCollectionTonProofFileId(undefined);
            setCollectionRemark("");
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setPaymentActionError("发起收款失败");
        } finally {
            setPaymentActionSubmitting(false);
        }
    };

    /** 收款记录操作完成后刷新详情、退款页签和操作日志。 */
    const refreshPaymentDetail = async () => {
        if (!order?.id) return;
        setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
        setOperationLogRefreshToken(current => current + 1);
        const result = await loadRefunds({ page: 1, size: 5, orderId: String(order.id) });
        setRefunds(result.list || []);
    };

    /** 提交旧 Vue 收款编辑弹窗的字段，并保持订单详情页上下文。 */
    const submitPaymentEdit = async (payload: ReceivePaymentUpdatePayload) => {
        if (!paymentTarget) return;
        try {
            await receivePaymentService.updatePayment(String(paymentTarget.id), {
                ...payload,
                orderId: String(paymentTarget.orderId),
                amount: payload.amount
            });
            notifyOperationSuccess("已修改");
            setEditPaymentOpen(false);
            setPaymentTarget(null);
            await refreshPaymentDetail();
        } catch (error) {
            showRequestError(error, "收款修改失败");
        }
    };

    /** 提交旧 Vue 确认付款弹窗，保留凭证和乐观锁版本号。 */
    const submitPaymentConfirm = async (payload: ConfirmReceivePaymentForm) => {
        try {
            await receivePaymentService.confirm(payload);
            notifyOperationSuccess("已确认");
            setConfirmPaymentOpen(false);
            setPaymentTarget(null);
            await refreshPaymentDetail();
        } catch (error) {
            showRequestError(error, "确认付款失败");
        }
    };

    /** 取消或撤回收款，并使用旧 Vue 的状态对应接口。 */
    const cancelPayment = async (payment: ReceivePayment) => {
        const isRevoke = Number(payment.state) === 5;
        if (!(await confirmDialog(isRevoke ? "是否撤回收款？" : "是否取消收款？"))) return;
        const payload: CancelReceivePaymentForm = { id: String(payment.id), reason: "" };
        try {
            if (isRevoke) {
                await receivePaymentService.revoke(payload);
                notifyOperationSuccess("已撤回");
            } else {
                await receivePaymentService.cancel(payload);
                notifyOperationSuccess("已取消");
            }
            await refreshPaymentDetail();
        } catch (error) {
            showRequestError(error, "收款取消失败");
        }
    };

    /** 删除已取消订单中的收款记录，保持 Vue 的二次确认文案。 */
    const deletePayment = async (payment: ReceivePayment) => {
        if (!(await confirmDialog("是否删除，删除后支付记录不可恢复", "是否删除"))) return;
        try {
            await receivePaymentService.delete(String(payment.id));
            notifyOperationSuccess("已删除");
            await refreshPaymentDetail();
        } catch (error) {
            showRequestError(error, "收款删除失败");
        }
    };

    /** 提交详情页新增退款，保持 Vue 退款表单的订单、类型和金额字段。 */
    const submitRefund = async () => {
        if (!order?.id || !refundType.trim()) {
            setPaymentActionError("请输入退款类型");
            return;
        }
        if (!refundAmount || refundAmount <= 0) {
            setPaymentActionError("请输入退款金额");
            return;
        }
        setPaymentActionSubmitting(true);
        setPaymentActionError(null);
        try {
            const form: RefundForm = {
                orderId: String(order.id),
                refundType: refundType.trim(),
                amount: refundAmount,
                ...(refundRemark.trim() ? { remark: refundRemark.trim() } : {})
            };
            if (refundTargetId) {
                await refundService.updateInDialog(refundTargetId, form);
                message.success("修改退款成功");
            } else {
                await createRefund(form);
                message.success("新增退款成功");
            }
            setRefundOpen(false);
            setRefundTargetId(undefined);
            setRefundType("");
            setRefundAmount(undefined);
            setRefundRemark("");
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
            void loadRefunds({ page: 1, size: 5, orderId: String(order.id) }).then(result =>
                setRefunds(result.list || [])
            );
        } catch {
            setPaymentActionError("新增退款失败");
        } finally {
            setPaymentActionSubmitting(false);
        }
    };

    /** 旧 Vue refundHistory 的编辑入口，复用同一退款弹窗并提交 PUT。 */
    const openRefundEdit = (refund: Refund) => {
        setPaymentActionError(null);
        setRefundTargetId(String(refund.id));
        setRefundType(refund.refundType || "");
        setRefundAmount(refund.amount == null ? undefined : Number(refund.amount));
        setRefundRemark(refund.remark || "");
        setRefundOpen(true);
    };

    /** 旧 Vue refundHistory 的取消入口，取消后刷新订单退款列表。 */
    const cancelRefund = async (refund: Refund) => {
        if (!(await confirmDialog("是否取消退款？"))) return;
        try {
            await refundService.cancel(String(refund.id));
            if (order?.id) {
                const result = await loadRefunds({ page: 1, size: 5, orderId: String(order.id) });
                setRefunds(result.list || []);
            }
        } catch (error) {
            showRequestError(error, "取消退款失败");
        }
    };

    /** 预览收款凭证，沿用收款管理详情的文件服务地址。 */
    const previewPayment = async (payment: ReceivePayment) => {
        const fileId = (payment.paymentFileId || payment.customFileId || [])[0];
        if (!fileId) return;
        try {
            const response = await request.service.get<{ path?: string }>(`/dataService/admin/file/${fileId}`);
            if (response.data.path) window.open(response.data.path, "_blank");
        } catch (error) {
            showRequestError(error, "收款凭证加载失败");
        }
    };

    /** Vue 的修改操作打开同一个提货弹窗，并通过 vo2Form 回填当前提货记录。 */
    const openDeliveryEditor = (delivery: DeliveryVo) => {
        setEditingDelivery(delivery);
        setDeliveryDialogOpen(true);
    };

    useEffect(() => {
        if (!orderId) {
            setErrorMessage("缺少订单编号");
            return;
        }
        let disposed = false;
        void loadDetail(orderId)
            .then(result => {
                if (disposed) return;
                const normalizedDetail = normalizeTradeOrderDetail(result);
                setDetail(normalizedDetail);
                setErrorMessage(null);
                if (normalizedDetail.order.state === 8) {
                    setRefunds([]);
                    return;
                }
                // 旧 Vue 在加载订单详情后同步读取退款分页，空列表时不生成退款页签。
                void loadRefunds({ page: 1, size: 5, orderId: String(normalizedDetail.order.id) })
                    .then(refundResult => {
                        if (!disposed) setRefunds(refundResult.list || []);
                    })
                    .catch(() => {
                        if (!disposed) setRefunds([]);
                    });
            })
            .catch(() => {
                if (!disposed) setErrorMessage("交易订单详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, loadRefunds, orderId]);

    useEffect(() => {
        if (!detail || ![2, 3, 4, 5].includes(Number(detail.order.state))) return;
        void loadInvoices({ page: 1, size: 100, orderId: String(detail.order.id) })
            .then(result => setInvoices(result.list || []))
            .catch(() => setInvoices([]));
    }, [detail, loadInvoices]);

    const order = detail?.order;
    const paymentCountdown = getPaymentCountdown(detail?.receivePayments, restTimeKey);

    // 倒计时归零时只刷新一次详情，以同步旧 Vue 在支付截止点重新拉取订单状态的行为。
    useEffect(() => {
        if (!paymentCountdown?.isEnd || !order?.id) return;
        const refreshKey = `${paymentCountdown.paymentId}:${paymentCountdown.payEndTime}`;
        if (refreshedExpiredPaymentRef.current === refreshKey) return;
        refreshedExpiredPaymentRef.current = refreshKey;
        void loadDetail(order.id)
            .then(result => setDetail(normalizeTradeOrderDetail(result)))
            .catch(() => undefined);
    }, [loadDetail, order?.id, paymentCountdown?.isEnd, paymentCountdown?.payEndTime, paymentCountdown?.paymentId]);
    const depositAmount =
        order?.productInfo?.settlementMethod === SETTLEMENT_METHOD.PAY_DEPOSIT.code
            ? detail?.receivePayments?.find(
                  payment =>
                      Number(payment.paymentType) === RECEIVE_PAYMENT_TYPE.ORDER_DEPOSIT.code &&
                      [RECEIVE_STATE.PAID.code, RECEIVE_STATE.OFFLINE_PAID.code].includes(Number(payment.state))
              )?.amount
            : undefined;
    const canAssign = Boolean(order && order.state !== 5 && currentPermissions.includes("ROLE_TRADE_ORDER_MANAGE"));
    const detailTabs = detail ? getTradeOrderDetailTabs(detail, refunds) : [];

    /** 刷新旧版提货明细独立分页数据，新增/修改/状态变更后复用。 */
    const refreshDeliveryList = async () => {
        if (!order?.id) return;
        try {
            const result = await loadDeliveries({ page: 1, size: 10, orderId: String(order.id) });
            setDeliveryRows(result.list || []);
        } catch {
            setDeliveryRows([]);
        }
    };

    /** 旧 Vue deliveryManagement 的发起提货动作。 */
    const initiateDelivery = async (delivery: DeliveryVo) => {
        const id = delivery.delivery?.id;
        if (!id || !(await confirmDialog("是否向买家发起提货"))) return;
        try {
            await deliveryService.initiateDelivery(String(id));
            await refreshDeliveryList();
        } catch (error) {
            showRequestError(error, "发起提货失败");
        }
    };

    /** 旧 Vue deliveryManagement 的取消提货动作。 */
    const cancelDelivery = async (delivery: DeliveryVo) => {
        const id = delivery.delivery?.id;
        if (!id || !(await confirmDialog("是否取消提货，取消后提货数据不可更改"))) return;
        try {
            await deliveryService.cancelDelivery(String(id));
            await refreshDeliveryList();
        } catch (error) {
            showRequestError(error, "取消提货失败");
        }
    };

    /** 旧 Vue deliveryManagement 的删除提货动作。 */
    const deleteDelivery = async (delivery: DeliveryVo) => {
        const id = delivery.delivery?.id;
        if (!id || !(await confirmDialog("是否删除提货，删除后该数据将会消失"))) return;
        try {
            await deliveryService.deleteDelivery(String(id));
            await refreshDeliveryList();
        } catch (error) {
            showRequestError(error, "删除提货失败");
        }
    };

    /** 复用旧 zhInsetTabs 的点击后平滑滚动行为，当前已迁移面板按稳定 ID 定位。 */
    const changeDetailTab = (key: string) => {
        setActiveTab(key);
        const target = document.getElementById(`trade-order-detail-tab-${key}`);
        if (target && typeof target.scrollIntoView === "function") {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <section aria-label="trade-order-detail-page" className="flex h-full min-h-full flex-col">
            <>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {order ? (
                    <>
                        <div className="section-card">
                            <ZhDetailHeader
                                leftInfos={[
                                    { label: "订单编号", value: order.id || "-" },
                                    { label: "下单时间", value: displayDate(order.createdTime || order.orderTime) },
                                    { label: "订单类型", value: orderTypeLabels[order.orderType] || "-" }
                                ]}
                                rightInfos={[
                                    { label: "更新时间", value: displayDate(order.updatedTime) },
                                    { label: "订单专员", value: order.handlerName || "-" },
                                    ...(paymentCountdown
                                        ? [
                                              {
                                                  label: `支付${
                                                      paymentCountdown.paymentType ===
                                                      RECEIVE_PAYMENT_TYPE.ORDER_DEPOSIT.code
                                                          ? "定金"
                                                          : "货款"
                                                  }剩余时间`,
                                                  value: paymentCountdown.formattedTime
                                              }
                                          ]
                                        : [])
                                ]}
                                stateDesc={`${orderStateLabels[order.state] || "-"}${order.auditState ? `（${orderAuditStateLabels[order.auditState] || "-"}）` : ""}`}
                                title="订单详情"
                            />
                            <ZhTabs
                                activeKey={activeTab}
                                className="mt-4"
                                items={detailTabs}
                                onChange={changeDetailTab}
                            />
                        </div>
                        <div className="trade-order-detail-content">
                            <div className="trade-order-message mt-4" id="trade-order-detail-tab-1">
                                <div className="section-card order-info">
                                    <div className="section-title mb-5">订单信息</div>
                                    <ZhGrid cols={2} gap={[20, 12]}>
                                        <ZhBaseItem colon={false} label="采购账号">
                                            {order.customerId ? (
                                                <Link
                                                    className="trade-order-detail__link"
                                                    to={`/lianYunUser/userInfo?userId=${order.customerId}`}
                                                >
                                                    {`${getEnterpriseName(order.customerEnterprise)}(${order.customerEnterprise?.mobile || "-"})`}
                                                </Link>
                                            ) : (
                                                <span className="trade-order-detail__link">
                                                    {`${getEnterpriseName(order.customerEnterprise)}(${order.customerEnterprise?.mobile || "-"})`}
                                                </span>
                                            )}
                                        </ZhBaseItem>
                                        <ZhBaseItem colon={false} label="收货信息" value={getOrderAddress(order)} />
                                        <ZhBaseItem colon={false} label="交易单价">
                                            <span>{fmtMoney(order.unitPrice, "元/吨")}</span>
                                            {Number(order.productInfo?.priceType) === ORDER_PRICE_TYPE.PRICE_ESTIMATED.code ? (
                                                <Tooltip
                                                    title="预估价格：价格可能存在变动，以提货当日价格为准"
                                                    placement="top"
                                                    overlayInnerStyle={{ whiteSpace: "nowrap" }}
                                                >
                                                    <span className="trade-order-estimated-price">
                                                        <i aria-hidden="true" className="iconfont icon-a-Frame38" />
                                                        <span>预估价格</span>
                                                    </span>
                                                </Tooltip>
                                            ) : null}
                                        </ZhBaseItem>
                                        <ZhBaseItem
                                            colon={false}
                                            label="采购数量"
                                            value={order.ton ? `${order.ton.toLocaleString("en-US")}吨` : "--"}
                                        />
                                        <ZhBaseItem
                                            colon={false}
                                            label="预估金额"
                                            value={
                                                order.estimatedAmount == null
                                                    ? "--"
                                                    : `${fmtMoney(order.estimatedAmount)}元`
                                            }
                                        />
                                        <ZhBaseItem
                                            colon={false}
                                            label="下单时间"
                                            value={displayDate(order.orderTime)}
                                        />
                                        <ZhBaseItem
                                            colon={false}
                                            label="锁货时间"
                                            value={displayDate(order.lockingTime)}
                                        />
                                        <ZhBaseItem colon={false} label="订单备注" value={order.remark || "--"} />
                                    </ZhGrid>
                                </div>
                                <div className="section-card order-product">
                                    <div className="section-title mb-5">订单商品</div>
                                    <div className="trade-order-product">
                                        <div className="trade-order-product__left">
                                            <div className="trade-order-product__image">
                                                {detail.mainFile?.path ? (
                                                    <img alt="商品图片" src={detail.mainFile.path} />
                                                ) : null}
                                            </div>
                                            <div className="trade-order-product__info">
                                                <div className="trade-order-product__name">
                                                    {order.productInfo?.title || "-"}
                                                </div>
                                                <ZhGrid cols={2} gap={[20, 12]}>
                                                    <ZhBaseItem
                                                        colon={false}
                                                        label="品类"
                                                        value={order.productInfo?.productTypeName || "-"}
                                                    />
                                                    <ZhBaseItem
                                                        colon={false}
                                                        label="规格"
                                                        value={order.productInfo?.productTypeSpec || "-"}
                                                    />
                                                    <ZhBaseItem
                                                        colon={false}
                                                        label="供应商"
                                                        value={getEnterpriseName(order.productInfo?.supplierEnterprise)}
                                                    />
                                                    <ZhBaseItem colon={false} label="商品编号">
                                                        {getTradeOrderProductDetailPath(
                                                            order.productId,
                                                            order.orderType
                                                        ) ? (
                                                            <Link
                                                                className="trade-order-detail__link"
                                                                to={
                                                                    getTradeOrderProductDetailPath(
                                                                        order.productId,
                                                                        order.orderType
                                                                    ) as string
                                                                }
                                                            >
                                                                {order.productId || "-"}
                                                            </Link>
                                                        ) : (
                                                            <span className="trade-order-detail__link">
                                                                {order.productId || "-"}
                                                            </span>
                                                        )}
                                                    </ZhBaseItem>
                                                </ZhGrid>
                                            </div>
                                        </div>
                                        {order.orderOrigin !== 1 ? null : (
                                            <button
                                                type="button"
                                                className="trade-order-product__snapshot"
                                                onClick={() => setSnapshotOpen(true)}
                                            >
                                                查看交易快照{" "}
                                                <i aria-hidden="true" className="iconfont icon-arrow-rifht" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {detail ? (
                                order && order.state !== 8 ? (
                                    <TradeOrderPaymentPanel
                                        canDeal={currentPermissions.includes("ROLE_TRADE_ORDER_DEAL")}
                                        order={detail.order}
                                        payments={detail.receivePayments || []}
                                        refunds={refunds}
                                        onAddRefund={() => {
                                            setPaymentActionError(null);
                                            setRefundOpen(true);
                                        }}
                                        onInitiateCollection={() => {
                                            setPaymentActionError(null);
                                            setCollectionType(undefined);
                                            setCollectionDeliveryId(undefined);
                                            setCollectionTon(undefined);
                                            setCollectionTonProofFileId(undefined);
                                            setCollectionAmount(undefined);
                                            setCollectionRemark("");
                                            setCollectionOpen(true);
                                        }}
                                        onPreviewPayment={payment => void previewPayment(payment)}
                                        onEditPayment={payment => {
                                            setPaymentTarget(payment);
                                            setEditPaymentOpen(true);
                                        }}
                                        onConfirmPayment={payment => {
                                            setPaymentTarget(payment);
                                            setConfirmPaymentOpen(true);
                                        }}
                                        onCancelPayment={payment => void cancelPayment(payment)}
                                        onDeletePayment={payment => void deletePayment(payment)}
                                        banButton={banButton}
                                        isPaymentHandler={
                                            detail.order.handlerId != null &&
                                            String(detail.order.handlerId) ===
                                                String(useUserStore.getState().userInfo.user?.id)
                                        }
                                    />
                                ) : null
                            ) : null}
                            {detail && refunds.length ? (
                                <TradeOrderRefundPanel
                                    banButton={banButton}
                                    canDeal={currentPermissions.includes("ROLE_TRADE_ORDER_DEAL")}
                                    onAdd={() => {
                                        setPaymentActionError(null);
                                        setRefundTargetId(undefined);
                                        setRefundType("");
                                        setRefundAmount(undefined);
                                        setRefundRemark("");
                                        setRefundOpen(true);
                                    }}
                                    onCancel={refund => void cancelRefund(refund)}
                                    onEdit={openRefundEdit}
                                    refunds={refunds}
                                    totalAmount={order?.refundAmount}
                                />
                            ) : null}
                            {detail && order && [2, 3, 4, 5].includes(order.state) ? (
                                <section className="section-card mt-4" id="trade-order-detail-tab-5">
                                    <div className="section-title mb-5">发票信息</div>
                                    <ZhTable<Invoice>
                                        data={invoices}
                                        columns={[
                                            {
                                                key: "id",
                                                label: "开票编号",
                                                prop: "id",
                                                render: ({ row }) => (
                                                    <Link
                                                        to={`/tradeManage/invoiceDetail?id=${row.id}&time=${Date.now()}`}
                                                    >
                                                        {row.id}
                                                    </Link>
                                                )
                                            },
                                            {
                                                key: "amount",
                                                label: "开票金额(元)",
                                                prop: "amount",
                                                render: ({ row }) => row.amount
                                            },
                                            {
                                                key: "invoiceDate",
                                                label: "开票日期",
                                                prop: "invoiceDate",
                                                render: ({ row }) => displayDate(row.invoiceDate)
                                            },
                                            {
                                                key: "state",
                                                label: "状态",
                                                prop: "state",
                                                render: ({ row }) =>
                                                    ({ 1: "已开票", 2: "已作废" })[Number(row.state)] || "-"
                                            },
                                            {
                                                key: "operation",
                                                label: "操作",
                                                render: ({ row }) =>
                                                    row.invalidFileId || row.fileId ? (
                                                        <Button
                                                            aria-label={`预览发票${row.id}`}
                                                            onClick={() =>
                                                                window.open(
                                                                    `/dataService/admin/file/${row.invalidFileId || row.fileId}`,
                                                                    "_blank"
                                                                )
                                                            }
                                                        >
                                                            预览
                                                        </Button>
                                                    ) : null
                                            }
                                        ]}
                                        pagination={false}
                                        rowKey={invoice => String(invoice.id)}
                                    />
                                </section>
                            ) : null}
                            {detail && shouldShowTradeOrderDeliveryInfo(detail) ? (
                                <>
                                    <TradeOrderDeliveryInfoPanel detail={detail} />
                                    {Number(detail.order.orderOrigin) === 1 ? (
                                        <TradeOrderSchedulePanel detail={detail} />
                                    ) : null}
                                </>
                            ) : null}
                            {detail && shouldShowTradeOrderDeliveryDetail(detail) ? (
                                <div id="trade-order-detail-tab-3">
                                    {Number(detail.order.orderType) !== ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code ? (
                                        <TradeOrderDeliveryListPanel
                                            banButton={banButton}
                                            canDeal={currentPermissions.includes("ROLE_TRADE_ORDER_DEAL")}
                                            deliveries={deliveryRows}
                                            onCancelDelivery={delivery => void cancelDelivery(delivery)}
                                            onAddDelivery={() => {
                                                setEditingDelivery(undefined);
                                                setDeliveryDialogOpen(true);
                                            }}
                                            onDeleteDelivery={delivery => void deleteDelivery(delivery)}
                                            onInitiateDelivery={delivery => void initiateDelivery(delivery)}
                                            onOpenDelivery={openDeliveryEditor}
                                            onRefreshDeliveries={() => void refreshDeliveryList()}
                                            orderId={String(detail.order.id)}
                                            orderOrigin={Number(detail.order.orderOrigin)}
                                            orderState={Number(detail.order.state)}
                                        />
                                    ) : null}
                                    {/* 保留履约详情作为团购/捡漏路径的真实数据兜底；寄售路径由上方旧版明细表承载视觉。 */}
                                    <div
                                        className={
                                            Number(detail.order.orderType) !==
                                            ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code
                                                ? "trade-order-delivery-fallback"
                                                : ""
                                        }
                                    >
                                        <DeliveryFulfillmentPanel deliveries={detail.deliveryVos || []} />
                                    </div>
                                </div>
                            ) : null}
                            {detail ? (
                                <TradeOrderRemarksPanel
                                    remarks={detail.remarks || []}
                                    onDelete={id => void handleDeleteOrderRemark(id)}
                                    onEdit={openOrderRemark}
                                />
                            ) : null}
                            {order ? (
                                <OperationHistoryPanel
                                    bizNo={order.id}
                                    module={
                                        order.orderOrigin === 2
                                            ? Operation_History_Enum.RECORD_ORDER
                                            : Operation_History_Enum.TRADE_ORDER
                                    }
                                    refreshToken={operationLogRefreshToken}
                                />
                            ) : null}
                        </div>
                    </>
                ) : null}
                {order ? (
                    <Space className="operation-fixed-btn" size={0}>
                        {order.orderOrigin !== 1 &&
                        !(order.auditState === 1 && !order.state) &&
                        currentPermissions.includes("ROLE_TRADE_ORDER_DEAL") ? (
                            <Button disabled={banButton}>
                                <Link aria-label="编辑订单" to={`/tradeManage/tradeOrder/edit?id=${order.id}`}>
                                    编辑
                                </Link>
                            </Button>
                        ) : null}
                        {canAssign ? (
                            <Button
                                aria-label="指派订单专员"
                                disabled={banButton}
                                onClick={() => void openAssignDialog()}
                            >
                                {order.handlerId ? "变更指派" : "指派"}
                            </Button>
                        ) : null}
                        {[2, 3].includes(order.state) && currentPermissions.includes("ROLE_TRADE_ORDER_MANAGE") ? (
                            <Button aria-label="完成订单" disabled={banButton} onClick={() => void openFinishOrder()}>
                                完成
                            </Button>
                        ) : null}
                        {currentPermissions.includes("ROLE_TRADE_ORDER_DEAL") ? (
                            <Button aria-label="订单备注" disabled={banButton} onClick={() => openOrderRemark()}>
                                订单备注
                            </Button>
                        ) : null}
                        {currentPermissions.includes("ROLE_TRADE_ORDER_CANCEL") &&
                        shouldShowTradeOrderDetailCancel(order, detail.bargainEndFlag) ? (
                            <Button disabled={banButton} onClick={handleCancelOrder}>
                                取消订单
                            </Button>
                        ) : null}
                        {order.beforeFinishState === 2 &&
                        order.state === 4 &&
                        currentPermissions.includes("ROLE_TRADE_ORDER_MANAGE") ? (
                            <Button
                                aria-label="修改提货吨数"
                                onClick={() => {
                                    setEditPickedUpTonError(null);
                                    setEditPickedUpTonOpen(true);
                                }}
                            >
                                修改提货吨数
                            </Button>
                        ) : null}
                        {[4, 5].includes(order.state) && currentPermissions.includes("ROLE_TRADE_ORDER_DELETE") ? (
                            <Button aria-label="删除订单" disabled={banButton} onClick={() => void handleDeleteOrder()}>
                                删除
                            </Button>
                        ) : null}
                        {/* 审核失败的补录订单沿用 Vue 的订单处理权限编码，允许处理人员删除重录。 */}
                        {order.auditState && !order.state && currentPermissions.includes("ROLE_TRADE_ORDER_DEAL") ? (
                            <Button aria-label="删除订单" disabled={banButton} onClick={() => void handleDeleteOrder()}>
                                删除
                            </Button>
                        ) : null}
                        <Button aria-label="返回" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                ) : null}
            </>
            {order ? (
                <TradeOrderDeliveryDialog
                    editingDelivery={editingDelivery}
                    open={deliveryDialogOpen}
                    order={order}
                    onCancel={() => {
                        setDeliveryDialogOpen(false);
                        setEditingDelivery(undefined);
                    }}
                    onSaved={async () => {
                        setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
                        await refreshDeliveryList();
                        setOperationLogRefreshToken(current => current + 1);
                    }}
                />
            ) : null}
            <ZhDialog
                modalClass="trade-order-remark-dialog"
                open={remarkOpen}
                title="订单备注"
                width={400}
                onCancel={() => setRemarkOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setRemarkOpen(false)}>取消</Button>
                        <Button aria-label="确认备注" type="primary" onClick={() => void submitOrderRemark()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                {remarkError ? <Alert className="mb-4" message={remarkError} role="alert" type="error" /> : null}
                <div className="trade-order-remark-form-item">
                    <label htmlFor="trade-order-remark-file-id">
                        <span className="trade-order-required-mark">*</span>附件
                    </label>
                    <div>
                        <ZhUpload
                            action="#"
                            aria-label="备注附件"
                            className="trade-order-remark-attachment"
                            httpRequest={options => void uploadOrderRemarkFile(options)}
                            limit={1}
                            showFileList={false}
                        >
                            {remarkFileName ? (
                                <span className="trade-order-remark-attachment__uploaded">
                                    <Button
                                        aria-label="查看/下载备注附件"
                                        className="trade-order-remark-attachment__link"
                                        htmlType="button"
                                        type="link"
                                        onClick={event => {
                                            event.stopPropagation();
                                            if (remarkFilePath) previewOrderRemarkFile();
                                        }}
                                    >
                                        查看/下载
                                    </Button>
                                    <span
                                        aria-label="删除附件"
                                        className="iconfont icon-close trade-order-remark-attachment__remove"
                                        role="button"
                                        tabIndex={0}
                                        onClick={event => {
                                            event.stopPropagation();
                                            removeOrderRemarkFile();
                                        }}
                                        onKeyDown={event => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                removeOrderRemarkFile();
                                            }
                                        }}
                                    />
                                </span>
                            ) : (
                                <Button className="trade-order-remark-attachment__link" htmlType="button" type="link">
                                    <span
                                        aria-hidden="true"
                                        className="iconfont icon-Frame-14 trade-order-remark-attachment__upload-icon"
                                    />
                                    上传
                                </Button>
                            )}
                        </ZhUpload>
                        <Input
                            id="trade-order-remark-file-id"
                            aria-label="附件文件ID"
                            className="sr-only"
                            placeholder="请输入附件文件ID"
                            value={remarkFileId}
                            onChange={event => setRemarkFileId(event.target.value)}
                        />
                    </div>
                </div>
                <div className="trade-order-remark-form-item">
                    <label htmlFor="trade-order-remark-content">备注</label>
                    <Input.TextArea
                        id="trade-order-remark-content"
                        aria-label="备注内容"
                        maxLength={200}
                        placeholder="如有说明，请备注"
                        rows={3}
                        showCount
                        value={remarkContent}
                        onChange={event => setRemarkContent(event.target.value)}
                    />
                </div>
            </ZhDialog>
            <ZhDialog
                open={assignOpen}
                modalClass="assign-dialog-modal"
                title="指派专员"
                width="842px"
                onCancel={() => setAssignOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignOpen(false)}>取消</Button>
                        <Button type="primary" onClick={() => void submitOrderAssign()}>
                            确认指派
                        </Button>
                    </Space>
                }
            >
                {assignError ? <Alert className="mb-4" message={assignError} role="alert" type="error" /> : null}
                <Select
                    allowClear
                    aria-label="订单专员"
                    options={commissioners.map(item => ({
                        label: `${item.user?.name || "-"}${item.orderCount == null ? "" : `（跟进 ${item.orderCount} 条）`}`,
                        value: String(item.user?.personId)
                    }))}
                    placeholder="请选择订单专员"
                    value={selectedHandlerId}
                    onChange={value => setSelectedHandlerId(value || "")}
                />
            </ZhDialog>
            <TradeOrderFinishDialog
                errorMessage={finishError}
                onCancel={() => {
                    if (!finishSubmitting) {
                        setFinishOpen(false);
                        setFinishError(null);
                    }
                }}
                onConfirm={form => void submitFinishOrder(form)}
                order={finishOpen ? detail : null}
                submitting={finishSubmitting}
                uploadSettlementFile={uploadTradeOrderSettlementFile}
            />
            <TradeOrderFinishDialog
                errorMessage={editPickedUpTonError}
                initialForm={{ pickedUpTon: order?.pickedUpTon, settlementIds: order?.settlementIds }}
                mode="edit"
                onCancel={() => {
                    if (!editPickedUpTonSubmitting) {
                        setEditPickedUpTonOpen(false);
                        setEditPickedUpTonError(null);
                    }
                }}
                onConfirm={form => void submitPickedUpTon(form)}
                order={editPickedUpTonOpen ? detail : null}
                submitting={editPickedUpTonSubmitting}
                uploadSettlementFile={uploadTradeOrderSettlementFile}
            />
            <TradeOrderCancelDialog
                errorMessage={cancelError}
                onCancel={() => {
                    if (!cancelSubmitting) {
                        setCancelOpen(false);
                        setCancelError(null);
                    }
                }}
                onConfirm={() => void submitCancelOrder()}
                order={cancelOpen && detail ? detail : null}
                submitting={cancelSubmitting}
            />
            <ZhDialog
                open={collectionOpen}
                modalClass="trade-order-collection-dialog"
                title="发起收款"
                width="800px"
                onCancel={() => {
                    if (!paymentActionSubmitting) setCollectionOpen(false);
                }}
                footer={
                    <Space>
                        <Button disabled={paymentActionSubmitting} onClick={() => setCollectionOpen(false)}>
                            取消
                        </Button>
                        <Button
                            loading={paymentActionSubmitting}
                            type="primary"
                            onClick={() => void submitCollection()}
                        >
                            确认
                        </Button>
                    </Space>
                }
            >
                {paymentActionError ? (
                    <Alert className="mb-4" message={paymentActionError} role="alert" type="error" />
                ) : null}
                <div className="trade-order-collection-form">
                    <div className="trade-order-collection-form__row">
                        <label htmlFor="trade-order-collection-delivery">关联提货</label>
                        <Select
                            id="trade-order-collection-delivery"
                            allowClear
                            showSearch
                            filterOption
                            placeholder="请输入关键词搜索提货"
                            value={collectionDeliveryId}
                            options={collectionDeliveryOptions.map(option => ({
                                value: String(option.delivery?.id),
                                label: `${option.delivery?.id || "--"} | ${displayDate(option.delivery?.scheduleDate)}`
                            }))}
                            onChange={value => {
                                setCollectionDeliveryId(value);
                                if (!value && collectionType !== RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.code) {
                                    setCollectionType(undefined);
                                }
                            }}
                        />
                        <span className="trade-order-collection-form__paid">
                            <span>已收款</span>
                            <strong>
                                {(() => {
                                    const paidAmount = collectionDeliveryOptions.find(
                                        option => String(option.delivery?.id) === String(collectionDeliveryId)
                                    )?.paidAmount;
                                    return paidAmount == null ? "--" : `${fmtMoney(paidAmount)}元`;
                                })()}
                            </strong>
                        </span>
                    </div>
                    <div className="trade-order-collection-form__row trade-order-collection-form__row--type">
                        <label>
                            <span className="trade-order-collection-form__required">*</span>费用类型
                        </label>
                        <Radio.Group
                            id="trade-order-collection-type"
                            value={collectionType}
                            onChange={event => setCollectionType(Number(event.target.value))}
                        >
                            {collectionPaymentTypeOptions.map(option => (
                                <Radio
                                    key={option.value}
                                    value={option.value}
                                    disabled={
                                        option.value !== RECEIVE_PAYMENT_TYPE.ORDER_PAYMENT.code &&
                                        !collectionDeliveryId
                                    }
                                >
                                    {option.label}
                                </Radio>
                            ))}
                        </Radio.Group>
                        <label htmlFor="trade-order-collection-amount">
                            <span className="trade-order-collection-form__required">*</span>支付金额
                        </label>
                        <InputNumber
                            id="trade-order-collection-amount"
                            min={0.01}
                            precision={2}
                            value={collectionAmount}
                            onChange={value => setCollectionAmount(value == null ? undefined : Number(value))}
                            addonAfter="元"
                        />
                    </div>
                    {collectionType === RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.code ||
                    collectionType === RECEIVE_PAYMENT_TYPE.WAREHOUSE_OPENING_PAYMENT.code ? (
                        <div className="trade-order-collection-form__row">
                            <label htmlFor="trade-order-collection-ton">发航吨位</label>
                            <InputNumber
                                id="trade-order-collection-ton"
                                min={0.01}
                                precision={2}
                                value={collectionTon}
                                onChange={value => setCollectionTon(value == null ? undefined : Number(value))}
                                addonAfter="吨"
                            />
                            {collectionType === RECEIVE_PAYMENT_TYPE.SHIPPING_PAYMENT.code ? (
                                <>
                                    <label>吨位证明</label>
                                    <ZhUpload
                                        action="#"
                                        limit={1}
                                        showFileList={false}
                                        httpRequest={async options => {
                                            try {
                                                const uploaded = await uploadTradeOrderSettlementFile(
                                                    options.file as Blob
                                                );
                                                setCollectionTonProofFileId(uploaded.id);
                                                options.onSuccess(uploaded);
                                            } catch (error) {
                                                options.onError(error instanceof Error ? error : new Error("上传失败"));
                                            }
                                        }}
                                    >
                                        <Button type="link">上传</Button>
                                    </ZhUpload>
                                </>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="trade-order-collection-form__row trade-order-collection-form__row--remark">
                        <label htmlFor="trade-order-collection-remark">费用说明</label>
                        <Input.TextArea
                            id="trade-order-collection-remark"
                            rows={3}
                            maxLength={200}
                            value={collectionRemark}
                            onChange={event => setCollectionRemark(event.target.value)}
                        />
                    </div>
                </div>
            </ZhDialog>
            <ZhDialog
                open={refundOpen}
                modalClass="trade-order-refund-dialog"
                title={refundTargetId ? "修改退款" : "新增退款"}
                width="600px"
                onCancel={() => {
                    if (!paymentActionSubmitting) setRefundOpen(false);
                }}
                footer={
                    <Space>
                        <Button disabled={paymentActionSubmitting} onClick={() => setRefundOpen(false)}>
                            取消
                        </Button>
                        <Button loading={paymentActionSubmitting} type="primary" onClick={() => void submitRefund()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                {paymentActionError ? (
                    <Alert className="mb-4" message={paymentActionError} role="alert" type="error" />
                ) : null}
                <div className="trade-order-refund-summary">
                    <span>
                        已收款金额 <strong>{fmtMoney(order?.payAmount)}元</strong>
                    </span>
                    {depositAmount == null ? null : (
                        <span>
                            定金金额 <strong>{fmtMoney(depositAmount)}元</strong>
                        </span>
                    )}
                    <span>
                        已退款金额 <strong>{fmtMoney(order?.refundAmount)}元</strong>
                    </span>
                </div>
                <div className="section-title trade-order-refund-section-title">退款信息</div>
                <div className="trade-order-refund-form">
                    <label htmlFor="trade-order-refund-type">
                        <span>
                            <i className="trade-order-refund-required">*</i>退款类型
                        </span>
                        <Input
                            id="trade-order-refund-type"
                            placeholder="退款类型"
                            value={refundType}
                            maxLength={25}
                            onChange={event => setRefundType(event.target.value)}
                        />
                    </label>
                    <label htmlFor="trade-order-refund-amount">
                        <span>
                            <i className="trade-order-refund-required">*</i>退款金额
                        </span>
                        <InputNumber
                            controls={false}
                            id="trade-order-refund-amount"
                            min={0.01}
                            precision={2}
                            value={refundAmount}
                            onChange={value => setRefundAmount(value == null ? undefined : Number(value))}
                            addonAfter="元"
                        />
                    </label>
                    <label className="is-full" htmlFor="trade-order-refund-remark">
                        <span>退款说明</span>
                        <Input.TextArea
                            id="trade-order-refund-remark"
                            placeholder="请输入退款说明"
                            rows={3}
                            maxLength={100}
                            value={refundRemark}
                            onChange={event => setRefundRemark(event.target.value)}
                        />
                    </label>
                </div>
            </ZhDialog>
            <EditPaymentDialog
                open={editPaymentOpen}
                data={paymentTarget || undefined}
                onCancel={() => {
                    setEditPaymentOpen(false);
                    setPaymentTarget(null);
                }}
                onSubmit={submitPaymentEdit}
            />
            <ConfirmPaymentDialog
                data={paymentTarget || undefined}
                open={confirmPaymentOpen}
                uploadFile={uploadTradeOrderPaymentVoucher}
                onCancel={() => {
                    setConfirmPaymentOpen(false);
                    setPaymentTarget(null);
                }}
                onSubmit={submitPaymentConfirm}
            />
            <PreviewPhone
                open={snapshotOpen}
                imageUrl={detail?.mainFile?.path}
                mainFile={detail?.mainFile}
                order={detail?.order}
                onClose={() => setSnapshotOpen(false)}
            />
        </section>
    );
}

export default TradeOrderDetailPage;
